import React from 'react';
import { useIntl } from 'react-intl';
import { Box, Button, TextField, Typography } from '@fairstone/ui/core';
import { t } from '@fairstone/ui/core/providers/Intl/locales/translate';

import ErrorView from 'components/errors/ErrorView';
import OTPResentModal from 'components/modals/OTPResentModal';
import { PrimaryButtonSto } from 'components/sto';

import styles from './EmailVerification.module.scss';

type Verification = {
    errorMessage: string;
    value: string;
    isValid: boolean | null;
};

interface EmailVerificationForm {
    apiError: string | null;
    codeResent: boolean;
    disabled: boolean;
    displayOtpModal: boolean;
    email: string;
    handleBlur?: () => void;
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    handleResend: () => void;
    setDisplayOtpModal: (arg: boolean) => void;
    verificationCode: Verification;
}

const EmailVerification = (props: EmailVerificationForm): React.ReactElement => {
    const { formatMessage } = useIntl();
    const {
        apiError,
        codeResent,
        disabled,
        displayOtpModal,
        email,
        handleBlur,
        handleChange,
        handleResend,
        handleSubmit,
        setDisplayOtpModal,
        verificationCode,
    } = props;

    return (
        <Box className={styles.emailVerification}>
            <Typography className={styles.pageDescription}>
                {t('general.email.verification.text', { email: email })}
            </Typography>
            <form className={styles.contentContainer} noValidate onSubmit={handleSubmit}>
                {apiError && <ErrorView error={apiError} />}
                <TextField
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        className: styles.textInput,
                        required: true,
                    }}
                    aria-required="true"
                    autoFocus
                    className={styles.textField}
                    error={!verificationCode.isValid}
                    fullWidth
                    helperText={
                        !verificationCode.isValid && verificationCode.errorMessage && t(verificationCode.errorMessage)
                    }
                    inputProps={{ maxLength: 6 }}
                    label={t('pages.verificationCode.field')}
                    name="code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder={formatMessage({
                        id: 'pages.verificationCode.placeholder',
                    })}
                    value={verificationCode.value}
                    variant="standard"
                />
                <PrimaryButtonSto
                    disabled={!verificationCode.isValid || disabled}
                    labelKey="general.submit"
                    type="submit"
                />
                <Box className={styles.resendContainer}>
                    <Typography variant="body2">{t('general.resend.code')}</Typography>
                    <Button className={styles.resendLink} color="secondary" onClick={handleResend}>
                        {t('general.resend.code.link')}
                    </Button>
                </Box>
            </form>
            <OTPResentModal
                email={email}
                onOk={() => setDisplayOtpModal(false)}
                otpSent={codeResent}
                visible={displayOtpModal}
            />
        </Box>
    );
};

export default EmailVerification;
